import { useMemo } from 'react';
import { MapIconStatic } from './MapIconStatic';

interface MapIconAnimatedProps {
  width?: number;
  height?: number;
  className?: string;
  duration?: number;
  easing?: string;
  color?: string;
  hoverColor?: string;
  squishFactor?: number; // Controls how much the icon squishes on hover
  strokeWidth?: number; // Added stroke width parameter
}

export const MapIconAnimated = ({
  width = 107,
  height = 107,
  className,
  duration = 0.5,
  easing = 'ease-in-out',
  color = 'currentColor',
  hoverColor = color,
  squishFactor = 0.15, // Default squish amount - adjust as needed
  strokeWidth = 8 // Default to match DashboardIconAnimated
}: MapIconAnimatedProps) => {
  // Calculate the squished paths based on the original MapIcon paths
  const pathData = useMemo(() => {
    // Original paths from MapIcon.tsx
    const mainPath =
      'M34.738 89.8862L21.7587 96.9387C15.9357 100.103 8.84766 95.8872 8.84766 89.2601L8.84766 32.1648C8.84766 28.651 10.7461 25.4113 13.8116 23.6938L32.7013 13.1108C36.4591 11.0055 40.9559 10.6715 44.9835 12.1985L64.2469 19.5019C66.8909 20.5043 69.8404 20.3044 72.325 18.9544L85.2824 11.9138C91.1054 8.74982 98.1934 12.9653 98.1934 19.5923V76.6636C98.1934 80.1902 96.2812 83.4396 93.1982 85.152L74.2906 95.6535C70.5604 97.7253 66.1094 98.0572 62.1132 96.5614L42.7775 89.3242C40.1428 88.3381 37.2099 88.5431 34.738 89.8862Z';
    const verticalLine1 = 'M38.6289 13.1265L38.6289 86.1999';
    const verticalLine2 = 'M68.4102 95.8925V22.0605';

    // A more robust function to squish SVG path
    // This properly handles SVG path commands and maintains connectivity
    const squishPath = (path: string, factor: number): string => {
      const centerX = 53.5; // Horizontal center of the icon

      // Parse SVG path into commands and coordinates
      const commands = path.match(/[a-zA-Z][^a-zA-Z]*/g) || [];
      let result = '';

      for (const cmd of commands) {
        const command = cmd[0]; // First character is the command letter
        const isRelative = command === command.toLowerCase();
        const coords = cmd
          .substring(1)
          .trim()
          .split(/[ ,]+/)
          .filter((c) => c !== '');

        // Add the command letter to the result
        result += command;

        // Process coordinates based on the command type
        switch (command.toUpperCase()) {
          case 'H': // Horizontal line - single X coordinate
            {
              const x = Number.parseFloat(coords[0]);
              const distFromCenter = isRelative ? x : x - centerX;
              const squish = distFromCenter * factor;
              const newX = isRelative ? x - squish : x - squish;
              result += newX.toFixed(4) + ' ';
            }
            break;

          case 'V': // Vertical line - not affected by horizontal squish
            result += coords.join(' ') + ' ';
            break;

          case 'M': // Move to
          case 'L': // Line to
            // These commands have X,Y coordinate pairs
            for (let i = 0; i < coords.length; i += 2) {
              if (i + 1 < coords.length) {
                const x = Number.parseFloat(coords[i]);
                const y = coords[i + 1];

                const distFromCenter = isRelative ? x : x - centerX;
                const squish = distFromCenter * factor;
                const newX = isRelative ? x - squish : x - squish;

                result += newX.toFixed(4) + ' ' + y + ' ';
              }
            }
            break;

          case 'C': // Cubic bezier - has multiple X,Y pairs
            for (let i = 0; i < coords.length; i += 2) {
              if (i + 1 < coords.length) {
                const x = Number.parseFloat(coords[i]);
                const y = coords[i + 1];

                const distFromCenter = isRelative ? x : x - centerX;
                const squish = distFromCenter * factor;
                const newX = isRelative ? x - squish : x - squish;

                result += newX.toFixed(4) + ' ' + y + ' ';
              }
            }
            break;

          default:
            // For other commands, keep as is
            result += coords.join(' ') + ' ';
        }
      }

      return result.trim();
    };

    // Create the squished versions of each path
    const squishMainPath = squishPath(mainPath, squishFactor);
    const squishVerticalLine1 = squishPath(verticalLine1, squishFactor);
    const squishVerticalLine2 = squishPath(verticalLine2, squishFactor);

    return {
      mainPath,
      verticalLine1,
      verticalLine2,
      squishMainPath,
      squishVerticalLine1,
      squishVerticalLine2
    };
  }, [squishFactor]);

  // Convert CSS ease-in-out to SVG ease equivalent
  const svgEasing = easing === 'ease-in-out' ? 'ease' : easing;

  // Animation configurations - updated to match DashboardIconAnimated
  const animationAttributes = {
    dur: `${duration * 2}s`,
    begin: 'mapParentGroup.mouseover',
    end: 'mapParentGroup.mouseout',
    repeatCount: 'indefinite',
    calcMode: 'spline',
    keySplines: '0.25 0.1 0.25 1; 0.25 0.1 0.25 1',
    keyTimes: '0; 0.5; 1',
    restart: 'whenNotActive'
  };

  return (
    <div className='group'>
      {/* Static version - visible by default, hidden on hover */}
      <div className={`group-hover:hidden ${className}`} style={{ width, height }}>
        <MapIconStatic
          width={width}
          height={height}
          color={color}
          strokeWidth={strokeWidth}
        />
      </div>

      {/* Animated version - hidden by default, visible on hover */}
      <div className={`hidden group-hover:block ${className}`} style={{ width, height }}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 107 108"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="mapParentGroup">
            {/* Background rect with animation transforms */}
            <rect
              width="106.808"
              height="106.808"
              fill="none"
              transform="translate(0 0.925293)"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 0.925293; 0 1.825293; 0 0.925293"
                {...animationAttributes}
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                additive="sum"
                values="0 53.5 54; 0.4 53.5 54; 0 53.5 54"
                {...animationAttributes}
              />
            </rect>

            {/* Main path animation */}
            <path
              d={pathData.mainPath}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.mainPath}; ${pathData.squishMainPath}; ${pathData.mainPath}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Vertical line 1 */}
            <path
              d={pathData.verticalLine1}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.verticalLine1}; ${pathData.squishVerticalLine1}; ${pathData.verticalLine1}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Vertical line 2 */}
            <path
              d={pathData.verticalLine2}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <animate
                attributeName="d"
                values={`${pathData.verticalLine2}; ${pathData.squishVerticalLine2}; ${pathData.verticalLine2}`}
                {...animationAttributes}
              />
              <animate
                attributeName="stroke"
                values={`${color}; ${hoverColor}; ${color}`}
                {...animationAttributes}
              />
            </path>

            {/* Hover capture */}
            <rect
              width="107"
              height="108"
              fill="transparent"
              style={{ cursor: 'pointer' }}
            />
          </g>
        </svg>
      </div>
    </div>
  );
};
